import { useEffect, useState, useReducer } from 'react';
import { useNotificationProvider } from 'hooks';
import userEditReducer from 'containers/pages/users/reducers/userEditReducer';
import { initUser, initValid } from 'containers/pages/users/data';
import { toggleAddressData } from 'containers/pages/contacts/data';
import {
	addCustName,
	addFileId,
	addImgPath,
} from 'containers/pages/users/libs';
import { checkUserValid, convertUser } from 'containers/pages/users/libs';
import {
	handleUser,
	makeUserObj,
	mergePhone,
} from 'containers/pages/users/libs';
import { getUserApi, getUsersApi, updateUserApi } from 'api';
import { useDispatch, useSelector } from 'react-redux';
import { selectAllCompanies } from 'redux/companies/companiesSlice';
import { selectAllContactTypes } from 'redux/contactTypes/contactTypesSlice';
import { selectAllRoleGroups } from 'redux/roleGroups/roleGroupsSlice';
import { storeUsers } from 'redux/users/usersSlice';
import { storeExistingContacts } from 'redux/userMgt/userMgtSlice';
import {
	storeSearchedUsers,
	storeSearchKeyword,
} from 'redux/userMgt/userMgtSlice';
import { selectAllLanguages } from 'redux/contacts/languagesSlice';
import { RESET, USER_CONVERT } from 'containers/pages/users/constants';
import { NOTIF_USER_UPDATE_SUCCESS } from 'constants/notificationMsg';
import deleteUser from 'api/users/deleteUser';

/**
 * @function useUserEditForm
 * @param {string} id
 * @param {function} handleMode
 * @param {function} onResetId
 * @returns {object}
 */
function useUserEditForm(id, handleMode, onResetId) {
	const reduxDispatch = useDispatch();
	const { companies } = useSelector(selectAllCompanies);
	const { roleGroups } = useSelector(selectAllRoleGroups);
	const { contactTypes } = useSelector(selectAllContactTypes);
	const { languages } = useSelector(selectAllLanguages);
	const { notifyError, notifySuccess } = useNotificationProvider();

	const [user, dispatch] = useReducer(userEditReducer, initUser);
	const [savedUser, setSavedUser] = useState(initUser); // Store fetched user
	const [isLoading, setIsLoading] = useState(false);
	const [valid, setValid] = useState(initValid);
	const [addressType, setAddressType] = useState(
		toggleAddressData.buttons[0].value,
	);
	const [pendingDeleteUserId, setPendingDeleteUserId] = useState('');

	const onToggleBtn = (event) => {
		setAddressType(event.target.value);
	};

	const onForm = (e, payload) => {
		handleUser(
			e,
			payload,
			user,
			addressType,
			dispatch,
			setValid,
			handleMode,
		);
	};

	const onValid = (key, error, msg) => {
		setValid((prev) => ({
			...prev,
			[key]: { isErr: error, errMsg: msg, canNotSave: error },
		}));
	};

	const onReset = () => {
		dispatch({ type: RESET, payload: savedUser }); // Reset to savedUser instead of initUser
		checkUserValid(savedUser, setValid, true);
		handleMode(true);
		reduxDispatch(storeExistingContacts([]));
	};

	const onInitUser = async () => {
		try {
			const tempUsers = await getUsersApi();
			const merged = await mergePhone(tempUsers);
			const _users = await addCustName(merged, companies);
			reduxDispatch(storeExistingContacts([]));
			reduxDispatch(storeUsers(_users));
			reduxDispatch(storeSearchedUsers(_users));
			reduxDispatch(storeSearchKeyword(''));
		} catch (error) {
			notifyError(error.message);
		}
	};

	const onUpdateUser = async () => {
		try {
			if (!validateBeforeSaving()) return;
			setIsLoading(true);
			let _user = makeUserObj(user);
			const added = await addFileId(_user);
			await updateUserApi(added);
			onInitUser();
			onResetId();
			notifySuccess(NOTIF_USER_UPDATE_SUCCESS);
		} catch (error) {
			notifyError(error.message);
		} finally {
			setIsLoading(false);
		}
	};

	const onClickDelete = (userId) => {
		setPendingDeleteUserId(userId);
	};

	const onConfirmDelete = async () => {
		try {
			setIsLoading(true);
			await deleteUser(pendingDeleteUserId);
			setPendingDeleteUserId('');
			onInitUser();
			onResetId();
			notifySuccess('User has been deleted successfully.');
		} catch (error) {
			// Check if the error response exists and inspect its status code
			if (error.response && error.response.status === 409) {
				notifyError(
					'User could not be deleted because there is data associated with it.',
				);
				setPendingDeleteUserId('');
			} else {
				notifyError(error.message || 'An unexpected error occurred.');
			}
		} finally {
			setIsLoading(false);
		}
	};

	const onCloseDeleteConfirmation = () => {
		setPendingDeleteUserId('');
	};

	const validateBeforeSaving = () => {
		const _canNotSave = checkUserValid(user, setValid, true);
		if (_canNotSave) {
			notifyError('Please correct the errors before saving.');
		}
		return !_canNotSave;
	};

	useEffect(() => {
		(async () => {
			try {
				setIsLoading(true);
				let tempUser = await getUserApi(id);
				const addedImgUser = await addImgPath(tempUser);
				const _user = convertUser(
					addedImgUser,
					companies,
					contactTypes,
					languages,
					roleGroups,
				);
				setSavedUser(_user); // Save fetched user for reset
				checkUserValid(_user, setValid, true);
				dispatch({ type: USER_CONVERT, payload: _user });
			} catch (error) {
				notifyError(error.message);
			} finally {
				setIsLoading(false);
			}
		})();
	}, [notifyError, id, companies, contactTypes, languages, roleGroups]);

	const state = {
		isLoading,
		user,
		valid,
		addressType,
		pendingDeleteUserId,
	};

	const handler = {
		onToggleBtn,
		onClickDelete,
		onForm,
		onValid,
		onReset,
		onUpdateUser,
		dispatch,
		onCloseDeleteConfirmation,
		onConfirmDelete,
	};

	return { state, handler };
}

export default useUserEditForm;
